/**
 * The web app's side of the session: where to sign in, and what to do when the session ends
 * while a page is open (a 401 from the app API): sign in again, then come back to that page.
 */

/** Where to go after signing in: a local path only, else `home`. */
export function safeNext(next: string | null | undefined, home = "/"): string {
  return next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/\\") ? next : home;
}

/** The web apps' sign-in page, unless the app names its own (crowroom's `/login`, crowdex's `/sign-in`). */
export const SIGNIN_PAGE = "/signin";

/** The sign-in page (`page`), coming back to `pathname + search` afterwards. */
export function signinPath(pathname: string, search = "", page = SIGNIN_PAGE): string {
  if (pathname === page) return page;
  return `${page}?next=${encodeURIComponent(safeNext(pathname + search))}`;
}

/** Sends the browser to sign in (at `page`) when the session is gone. */
export function redirectToSignin(
  location: Pick<Location, "pathname" | "search" | "assign"> = window.location,
  page = SIGNIN_PAGE,
): void {
  if (location.pathname === page) return;
  location.assign(signinPath(location.pathname, location.search, page));
}

/**
 * The query client's handling of an ended session, given what tells it (`isUnauthorized`: the app
 * API's 401 `unauthorized`, not another 401 such as a shared link's password): no retry, and the
 * browser goes to sign in (at `page`). `retries` is how many times other failures are tried again.
 *
 *     const expiry = sessionExpiry(isUnauthorized);
 *     new QueryClient({
 *       queryCache: new QueryCache({ onError: expiry.onError }),
 *       mutationCache: new MutationCache({ onError: expiry.onError }),
 *       defaultOptions: { queries: { retry: expiry.retry } },
 *     });
 */
export function sessionExpiry(isUnauthorized: (error: unknown) => boolean, retries = 1, page = SIGNIN_PAGE) {
  return {
    onError(error: unknown): void {
      if (isUnauthorized(error)) redirectToSignin(window.location, page);
    },
    retry(count: number, error: unknown): boolean {
      return !isUnauthorized(error) && count < retries;
    },
  };
}
